/* eslint-disable complexity */
import restAPI from "../http-common";
import session from "./session";
import filterData from "@/utils.js";
import { wasteWeightCostRequest, wastePerGuestPerServiceTimeRequest, performanceGraphDataRequest } from "./CancellableRequest";
import store from "@/store";

/*
 * TODO: split the api to multiple files
 * https://codetribellc.atlassian.net/browse/KIT-684
 */
export default class AdApi {

	static getQueryParams(isCompare, overrides = {}) {
		const activeCompare = store.getters["filters/activeCompareTo"],
			data = filterData.returnFiltersState(overrides),
			request = {
				start_date: data.dates[isCompare ? "start_date_compare" : "start_date_current"],
				end_date: data.dates[isCompare ? "end_date_compare" : "end_date_current"],
				outlet_id: data.activeOutletId,
				type: data.activeWasteType,
				device_id: data.activeDevicesIds
			};

		if (AdApi.areBaseOrAverage(isCompare)) {
			request.comparison_type = activeCompare;
			request.average = data.average;
		}

		return request;
	}

	static areBaseOrAverage(isCompare) {
		const activeCompare = store.getters["filters/activeCompareTo"];

		/*
		 * you don't want the base or average for the current request,
		 * you only want it for the compare request
		 */
		if (!isCompare)
			return false;


		if ([ "base_day", "average_day", "base_week", "average_week", "base_month", "average_month" ].includes(activeCompare))
			return true;


		return false;
	}

	getMe() {
		return restAPI({
			method: "get",
			url: "/me",
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getWasteWeightCost(compare) {
		wasteWeightCostRequest.cancel(compare);

		return restAPI({
			method: "get",
			url: "total-waste?",
			params: AdApi.getQueryParams(compare),
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {},
			cancelToken: wasteWeightCostRequest.generateCancelToken(compare)
		});
	}

	getPerformanceGraphData(compare) {
		performanceGraphDataRequest.cancel(compare);

		return restAPI({
			method: "get",
			url: AdApi.areBaseOrAverage(compare) ? "performance-graph-data-comparators?" : "performance-graph-data?",
			params: {...AdApi.getQueryParams(compare)},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {},
			cancelToken: performanceGraphDataRequest.generateCancelToken(compare)
		});
	}

	getWasteSourceAndServiceTimes() {
		return restAPI({
			method: "get",
			url: "/waste-sources-service-times?",
			params: AdApi.getQueryParams(),
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getWasteSource() {
		return restAPI({
			method: "get",
			url: "/waste-source?",
			params: AdApi.getQueryParams(),
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getWastePerCategoryItem(categoryId) {
		return restAPI({
			method: "get",
			url: "/group-breakdown?",
			params: {
				...AdApi.getQueryParams(),
				group_id: categoryId,
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getCategoryItemPerformanceGraph(labelId, compare) {
		const queryParams = AdApi.getQueryParams(compare);

		// These endpoints don't need to know the avoidability of the label
		delete queryParams.type;

		if (queryParams.start_date === "" && queryParams.end_date === "") {
			return new Promise((resolve) => resolve({
				data: [],
				status: 200
			}));
		}

		return restAPI({
			method: "get",
			url: AdApi.areBaseOrAverage(compare) ? "/label-performance-graph-comparators?" : "/label-performance-graph?",
			params: {
				...queryParams,
				label_id: labelId,
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getWasteSourceWithTranslations() {
		return restAPI({
			method: "get",
			url: "waste-sources-with-translations",
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getTrackedItemCard(labelId) {
		return restAPI({
			method: "get",
			url: "tracked-label-card",
			params: {
				label_id: labelId,
				...AdApi.getQueryParams(false)
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getTrackedItemActions(labelId) {
		return restAPI({
			method: "get",
			url: "tracked-label-actions",
			params: {
				label_id: labelId,
				...AdApi.getQueryParams(false)
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	setTrackedItemAction(labelId, date, description, type) {
		return restAPI({
			method: "post",
			url: "tracked-label-action",
			data: {
				outlet_id: store.getters["user/activeOutlet"].outlet_id,
				label_id: labelId,
				date,
				description,
				type
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	deleteTrackedItemAction(labelId, actionId) {
		return restAPI({
			method: "delete",
			url: "tracked-label-action",
			params: {
				outlet_id: store.getters["user/activeOutlet"].outlet_id,
				label_id: labelId,
				action_id: actionId
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	updateTrackedItemAction(labelId, actionId, date, description, type) {
		return restAPI({
			method: "put",
			url: "tracked-label-action",
			data: {
				outlet_id: store.getters["user/activeOutlet"].outlet_id,
				label_id: labelId,
				action_id: actionId,
				date: date,
				description,
				type
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getTrackedItemsPerformance() {
		return restAPI({
			method: "get",
			url: "tracked-labels-performance",
			params: AdApi.getQueryParams(false),
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getTrackedItemsTable() {
		return restAPI({
			method: "get",
			url: "tracked-labels-table",
			params: AdApi.getQueryParams(false),
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	setTrackedLabelNote({ labelId, note }) {
		return restAPI({
			method: "put",
			url: "set-tracked-label-note",
			data: {
				outlet_id: store.getters["user/activeOutlet"].outlet_id,
				label_id: labelId,
				note: note
			},
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {}
		});
	}

	getWastePerGuestPerServiceTime(compare) {
		wastePerGuestPerServiceTimeRequest.cancel(compare);

		return restAPI({
			method: "get",
			url: "average-waste-per-guest-per-service-time?",
			params: AdApi.getQueryParams(compare),
			headers: session.getIdToken() ? {"Authorization": session.getIdToken()} : {},
			cancelToken: wastePerGuestPerServiceTimeRequest.generateCancelToken(compare)
		});
	}
}

export const ApiService = new AdApi();
